import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPendingAutoevaluations } from "./funtionsBD/Mainservice";
import { useBrand } from "../context/BrandContext";
import "./AutoevalBanner.css";

function parseHex(hex) {
  const h = String(hex || "#46528b").replace("#", "");
  if (h.length !== 6) return { r: 70, g: 82, b: 139 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function relativeLuminance(r, g, b) {
  const [rs, gs, bs] = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function mixHexWithBlack(hex, blackRatio = 0.18) {
  const { r, g, b } = parseHex(hex);
  const mix = (channel) => Math.max(0, Math.min(255, Math.round(channel * (1 - blackRatio))));
  const toHex = (channel) => mix(channel).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function getSecondaryBannerPalette(secondaryHex) {
  const secondary = secondaryHex || "#46528b";
  const secondaryDark = mixHexWithBlack(secondary, 0.18);
  const bgLum = relativeLuminance(...Object.values(parseHex(secondaryDark)));
  const useDarkText = bgLum > 0.55;

  return {
    secondary,
    secondaryDark,
    text: useDarkText ? "#1E1E1E" : "#FFFFFF",
    textMuted: useDarkText ? "rgba(30, 30, 30, 0.82)" : "rgba(255, 255, 255, 0.88)",
    textShadow: useDarkText ? "none" : "0 1px 2px rgba(0, 0, 0, 0.22)",
    btnBg: useDarkText ? secondary : "#FFFFFF",
    btnText: useDarkText ? "#FFFFFF" : secondary,
  };
}

/** Player-capable user: pure player, dual coach+player, admin+player, etc. */
export function userCanUsePlayerAutoevaluation(user) {
  if (!user || typeof user !== "object") return false;
  if (user.hasPlayerRole === true) return true;
  if (user.type_user_id === 4) return true;
  if (Array.isArray(user.all_role_names)) {
    for (const n of user.all_role_names) {
      const s = String(n).toLowerCase().trim();
      if (s === "player" || s === "joueur" || /^player\b/.test(s)) return true;
    }
  }
  if (Array.isArray(user.all_roles) && user.all_roles.includes("r4")) return true;
  return false;
}

/** True if we should call the pending-evaluations API (dual coach+player often has type_user_id 3). */
export function shouldFetchPendingAutoevaluations(user) {
  if (userCanUsePlayerAutoevaluation(user)) return true;
  if (
    user.isDualRole === true &&
    user.hasCoachRole === true &&
    user.hasPlayerRole !== false
  ) {
    return true;
  }
  return false;
}

export const AutoevalBanner = () => {
  const [pendingCount, setPendingCount] = useState(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const mayHavePlayerEvals = shouldFetchPendingAutoevaluations(user);

  const bannerPalette = useMemo(
    () => getSecondaryBannerPalette(brand?.color_secondary),
    [brand?.color_secondary]
  );

  const bannerStyle = useMemo(
    () => ({
      "--ae-banner-secondary": bannerPalette.secondary,
      "--ae-banner-secondary-dark": bannerPalette.secondaryDark,
      "--ae-banner-text": bannerPalette.text,
      "--ae-banner-text-muted": bannerPalette.textMuted,
      "--ae-banner-text-shadow": bannerPalette.textShadow,
      "--ae-banner-btn-bg": bannerPalette.btnBg,
      "--ae-banner-btn-text": bannerPalette.btnText,
    }),
    [bannerPalette]
  );

  useEffect(() => {
    if (!mayHavePlayerEvals) return;
    const fetchCount = async () => {
      try {
        const response = await getPendingAutoevaluations();
        const required = response.data.required_pending;
        setPendingCount(
          required != null ? Number(required) || 0 : Number(response.data.total_pending) || 0
        );
      } catch {
        setPendingCount(0);
      }
    };
    fetchCount();
  }, [mayHavePlayerEvals]);

  if (!mayHavePlayerEvals || pendingCount === 0) return null;

  return (
    <div
      className="autoeval-banner"
      style={bannerStyle}
      onClick={() => navigate("/player-autoevaluation")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") navigate("/player-autoevaluation"); }}
    >
      <div className="autoeval-banner__left">
        <span className="autoeval-banner__icon" aria-hidden="true">&#9997;&#65039;</span>
        <div className="autoeval-banner__text-wrap">
          <p className="autoeval-banner__title">{t("ae_banner_title")}</p>
          <p className="autoeval-banner__msg">{t("ae_banner_msg", { count: pendingCount })}</p>
        </div>
      </div>
      <button
        type="button"
        className="autoeval-banner__btn"
        onClick={(e) => { e.stopPropagation(); navigate("/player-autoevaluation"); }}
      >
        {t("ae_banner_btn")}
      </button>
    </div>
  );
};
